import {
  IssueLiability,
  IssueLiabilityDoc,
  issueLiabilityResponsibleTypeMap,
  issueLiabilityStatusMap,
} from '@gooduncles/gu-app-schema';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Button, Descriptions, Form, Modal, Tag, message } from 'antd';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { selectValidPartnersUsers } from 'src/redux/slices/partners';
import { useAppSelector } from 'src/redux/store';
import { useAuth } from 'src/stores/auth-context';

import { formatDate } from 'src/utils/date-util';
import { observeIssueLiabilityDoc } from 'src/utils/firebase/firebase-short-cut';
import { updateIssueLiabilityWithStatusHistory } from 'src/utils/issue/issue-util';

import useIssueCase from 'src/hooks/swr/useIssueCase';
import useSWRProductSuppliers from 'src/hooks/swr/useSWRProductSuppliers';
import useSWRStores from 'src/hooks/swr/useSWRStores';
import useSWRUsers from 'src/hooks/swr/useSWRUsers';
import { formatDisplayName } from 'src/hooks/subscription/useStaffUsers';

import IssuePanelModal, { IssuePanelModalData } from 'src/components/Issue/IssuePanelModal';

import {
  LiabilityForm,
  LiabilityFormValues,
  LiabilityHistoryManager,
  formatAmount,
  liabilityStatusColorMap,
} from './components';

interface IssueLiabilityDetailModalProps {
  open: boolean;
  onClose: () => void;
  /** 손실 책임 문서 (초기 로딩용, 이후 실시간 구독) */
  liability: IssueLiabilityDoc;
}

/**
 * 단일 손실 책임 상세/편집 모달
 * - IssueLiabilityManagement 페이지에서 사용
 * - 단일 항목에 대한 상세 정보 표시 및 편집
 */
const IssueLiabilityDetailModal: FC<IssueLiabilityDetailModalProps> = ({
  open,
  onClose,
  liability: initialLiability,
}) => {
  const { user } = useAuth();
  const [form] = Form.useForm<LiabilityFormValues>();
  const [liability, setLiability] = useState<IssueLiabilityDoc>(initialLiability);
  const [isEditMode, setIsEditMode] = useState(false);

  // IssuePanelModal 상태
  const [issuePanelOpen, setIssuePanelOpen] = useState(false);
  const [issuePanelData, setIssuePanelData] = useState<IssuePanelModalData>(null);

  // 실시간 데이터 구독
  useEffect(() => {
    if (!open) return;

    const subscription = observeIssueLiabilityDoc(initialLiability._id).subscribe({
      next: (doc) => {
        if (doc) {
          setLiability(doc);
        }
      },
      error: (error) => {
        console.error('Failed to observe issue liability:', error);
      },
    });

    return () => subscription.unsubscribe();
  }, [open, initialLiability._id]);

  // IssueCase 조회 (매장명 표시용)
  const { issueCase } = useIssueCase(open ? initialLiability.issueCaseId : undefined);

  // 책임자 이름 조회용 데이터
  const { stores } = useSWRStores([['state', '!=', 'closed']]);
  const { users } = useSWRUsers([['role', 'in', ['manager', 'admin']]]);
  const partnersUsers = useAppSelector(selectValidPartnersUsers);
  const { productSuppliers } = useSWRProductSuppliers([['deletedAt', '==', null]]);

  const currentUserId = user?._id ?? '';

  // responsibleType 변경 감지
  const responsibleType = Form.useWatch('responsibleType', form);

  /**
   * 편집 모드 진입 시 폼 초기화
   */
  useEffect(() => {
    if (isEditMode && liability) {
      form.setFieldsValue({
        responsibleType: liability.responsibleType,
        responsibleId: liability.responsibleId,
        supplyAmount: liability.amount.supplyAmount,
        taxAmount: liability.amount.taxAmount,
        memo: liability.memo,
        progressNote: liability.progressNote,
        status: liability.status,
        costType: liability.costType,
      });
    }
  }, [isEditMode, liability, form]);

  /**
   * 책임자 이름 조회
   */
  const responsibleName = useMemo(() => {
    const { responsibleType, responsibleId } = liability;
    if (!responsibleId) return '-';

    switch (responsibleType) {
      case 'store': {
        const store = stores?.find((s) => s._id === responsibleId);
        return store?.storeNickname ?? store?.storeName ?? responsibleId;
      }
      case 'operator': {
        const user = users?.find((u) => u._id === responsibleId);
        return user ? formatDisplayName(user) : responsibleId;
      }
      case 'partners': {
        const partner = partnersUsers?.find((p) => p._id === responsibleId);
        return partner?.nickname ?? responsibleId;
      }
      case 'productSupplier': {
        const supplier = productSuppliers?.find((s) => s._id === responsibleId);
        return supplier?.name ?? responsibleId;
      }
      case 'etc':
      default:
        return responsibleId;
    }
  }, [liability, stores, users, partnersUsers, productSuppliers]);

  /**
   * 저장 핸들러
   * - 상태가 변경된 경우 히스토리에 자동으로 기록 (updateIssueLiabilityWithStatusHistory 사용)
   */
  const handleSave = useCallback(async () => {
    try {
      const values = await form.validateFields();
      const totalAmount = (values.supplyAmount ?? 0) + (values.taxAmount ?? 0);

      const updateData: Partial<IssueLiability> = {
        responsibleType: values.responsibleType,
        responsibleId: values.responsibleId || null,
        amount: {
          supplyAmount: values.supplyAmount ?? 0,
          taxAmount: values.taxAmount ?? 0,
          totalAmount,
        },
        memo: values.memo || null,
        progressNote: values.progressNote || null,
        status: values.status,
        costType: values.costType,
      };

      await updateIssueLiabilityWithStatusHistory(liability._id, updateData, liability, currentUserId);
      message.success('저장되었습니다.');
      setIsEditMode(false);
    } catch (error) {
      console.error('Save failed:', error);
      message.error('저장에 실패했습니다.');
    }
  }, [form, liability, currentUserId]);

  /**
   * 편집 취소 핸들러
   */
  const handleCancelEdit = useCallback(() => {
    form.resetFields();
    setIsEditMode(false);
  }, [form]);

  /**
   * 연결된 이슈 상세보기 열기
   */
  const handleOpenLinkedIssue = useCallback(() => {
    if (liability.issueCaseId) {
      setIssuePanelData({ issueId: liability.issueCaseId });
      setIssuePanelOpen(true);
    }
  }, [liability.issueCaseId]);

  /**
   * IssuePanelModal 닫기
   */
  const handleCloseIssuePanel = useCallback(() => {
    setIssuePanelOpen(false);
    setIssuePanelData(null);
  }, []);

  return (
    <>
      <Modal
        title='손실 책임 상세'
        open={open}
        onCancel={onClose}
        afterClose={() => {
          form.resetFields();
          setLiability(initialLiability);
          setIsEditMode(false);
        }}
        width={900}
        footer={
          isEditMode ? (
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button onClick={handleCancelEdit}>취소</Button>
              <Button type='primary' onClick={handleSave}>
                저장
              </Button>
            </div>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button onClick={onClose}>닫기</Button>
              <Button type='primary' onClick={() => setIsEditMode(true)}>
                편집
              </Button>
            </div>
          )
        }
      >
        <div style={{ maxHeight: 'calc(80vh - 150px)', overflowY: 'auto' }}>
          {/* 편집 불가능 영역 */}
          <Descriptions
            bordered
            size='small'
            column={2}
            style={{ marginBottom: 16 }}
            labelStyle={{ width: 120 }}
            contentStyle={{ width: 'calc(50% - 120px)' }}
          >
            <Descriptions.Item label='이슈 케이스' span={2}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontFamily: 'monospace' }}>{liability.issueCaseId}</span>
                <Tag
                  icon={<ExclamationCircleOutlined />}
                  color='orange'
                  onClick={handleOpenLinkedIssue}
                  style={{ cursor: 'pointer' }}
                >
                  이슈 상세
                </Tag>
              </div>
            </Descriptions.Item>
            <Descriptions.Item label='매장명'>{issueCase?.storeName ?? '-'}</Descriptions.Item>
            <Descriptions.Item label='등록일시'>
              {formatDate(liability.registeredAt, 'yyyy-MM-dd HH:mm')}
            </Descriptions.Item>
          </Descriptions>

          {isEditMode ? (
            /* 편집 폼 */
            <div style={{ marginBottom: 24 }}>
              <LiabilityForm
                form={form}
                responsibleType={responsibleType}
                isEditing={true}
                editingLiability={liability}
                showHeader={false}
                hideActionButtons={true}
              />
            </div>
          ) : (
            /* 편집 가능 영역 (읽기 전용 표시) */
            <Descriptions
              bordered
              size='small'
              column={2}
              style={{ marginBottom: 24 }}
              labelStyle={{ width: 120 }}
              contentStyle={{ width: 'calc(50% - 120px)' }}
            >
              <Descriptions.Item label='상태'>
                <Tag color={liabilityStatusColorMap[liability.status]}>{issueLiabilityStatusMap[liability.status]}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label='총액'>
                <span style={{ fontWeight: 600 }}>{formatAmount(liability.amount.totalAmount)}</span>
              </Descriptions.Item>
              <Descriptions.Item label='책임자'>
                {issueLiabilityResponsibleTypeMap[liability.responsibleType]} / {responsibleName}
              </Descriptions.Item>
              <Descriptions.Item label='공급가 / 세금'>
                {formatAmount(liability.amount.supplyAmount)} / {formatAmount(liability.amount.taxAmount)}
              </Descriptions.Item>
              <Descriptions.Item label='메모' span={2}>
                {liability.memo ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item label='진행 노트' span={2}>
                {liability.progressNote ?? '-'}
              </Descriptions.Item>
            </Descriptions>
          )}

          {/* 히스토리 관리 */}
          <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
            <LiabilityHistoryManager liability={liability} currentUserId={currentUserId} />
          </div>
        </div>
      </Modal>
      {/* 연결된 이슈 상세 모달 */}
      {issuePanelData && <IssuePanelModal open={issuePanelOpen} onClose={handleCloseIssuePanel} {...issuePanelData} />}
    </>
  );
};

export default IssueLiabilityDetailModal;
